import { Plug } from 'lucide-react';
import * as React from "react";
import { Badge } from "#app/components/ui/badge.tsx";
import { Card } from '#app/components/ui/card';
import { Icon, type IconName } from "#app/components/ui/icon.tsx";
import { Separator } from '#app/components/ui/separator';
import { getConnectorInfo, getNetworkName } from "#app/utils/connectors.ts";

// Define internal types
interface Outlet {
  id: number;
  connectorName: string | null;
  connector: string | null;
  kilowatts: number | null;
}

interface StationItem {
  id: number;
  networkId: number | null;
  outlets: Array<Outlet>;
}

interface ChargerType {
  networkId: string | null;
  connector: number;
  count: number;
}

// Update props
interface ConnectorListProps {
  stations: Array<StationItem> | undefined;
}

export function ConnectorList({ stations }: ConnectorListProps) {
  // Move calculation logic here
  let chargerTypes: ChargerType[] = [];
  if (stations && stations.length > 0) {
    stations.forEach((stationItem) => {
      const { networkId } = stationItem;
      const networkIdStr = networkId !== null ? networkId.toString() : null;
      stationItem.outlets.forEach((outlet) => {
        const { connector } = outlet;
        if (connector != null) {
          const connectorNum = parseInt(connector, 10);
          if (!isNaN(connectorNum)) {
            let entry = chargerTypes.find(
              (e) => e.networkId === networkIdStr && e.connector === connectorNum,
            );
            if (entry) {
              entry.count += 1;
            } else {
              chargerTypes.push({
                networkId: networkIdStr,
                connector: connectorNum,
                count: 1,
              });
            }
          }
        }
      });
    });
  }

  const totalNumberOfChargers = chargerTypes.reduce(
    (sum, { count }) => sum + count,
    0,
  );

  // Original return logic
  if (totalNumberOfChargers <= 0) {
    return null;
  }

  // Adjusted return logic to match StationInfo style
  return (
    <Card className='overflow-hidden bg-white shadow-sm backdrop-blur-sm'>
      <div className='p-5'>
        <div className='mb-4 flex items-center gap-2'>
          <Plug className='h-5 w-5 text-primary' />
          <h3 className='text-lg font-semibold'>
            Conectores ({totalNumberOfChargers})
          </h3>
        </div>
        <div className='space-y-4'>
          {chargerTypes.map((chargerType, index) => {
            const connectorInfo = getConnectorInfo(chargerType.connector);
            const networkName = chargerType.networkId
              ? getNetworkName(chargerType.networkId)
              : null;
            const iconName = (connectorInfo?.icon ?? 'charging-station') as IconName;

            return (
              <React.Fragment
                key={`${chargerType.networkId}-${chargerType.connector}`}
              >
                <div className='group flex items-center justify-between gap-3'>
                  <div className='flex flex-1 items-center gap-3'>
                    <Icon
                      className='h-10 w-10 text-muted-foreground/70 transition-colors group-hover:text-primary md:h-12 md:w-12'
                      name={iconName}
                    />
                    <div className='flex-1 space-y-1'>
                      <p className='font-semibold text-sm text-foreground/90'>
                        {connectorInfo?.name ?? `Tipo ${chargerType.connector}`}
                      </p>
                      {networkName && (
                        <p className='text-sm text-muted-foreground'>
                          {networkName}
                        </p>
                      )}
                    </div>
                  </div>

                  <Badge
                    variant='outline'
                    className='flex min-w-[80px] items-center justify-center text-sm dark:border-gray-600 dark:text-gray-300'
                  >
                    {chargerType.count}{" "}
                    {chargerType.count > 1 ? 'plugs' : 'plug'}
                  </Badge>
                </div>
                {index < chargerTypes.length - 1 && (
                  <Separator className='bg-border/40' />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
